import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Button,
  FormGroup,
  Label,
  Container,
} from "reactstrap";
import { Link } from "react-router-dom";
import DataTable from "react-data-table-component";
import Select from "react-select";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import NumberCommas from "../../Components/NumberCommas/NumberCommas";
import PrintHeader from "../../Components/PrintHeader/PrintHeader";
import PrintableTable from "./Components/PrintableTable";
import SupplierServices from "../../Services/QRCodeServices/SupplierServices/SupplierServices";
import ItemWiseServices from "../../Services/QRCodeServices/ItemWiseServices/ItemWiseServices";

const ItemWiseReport = () => {
  const [supplierItems, setSupplierItems] = useState([]);
  const [supplierID, setSupplierID] = useState([]);
  const [dataS, setDataS] = useState("");
  const [loading, setLoading] = useState(false);
  const [isReportGenerated, setIsReportGenerated] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [selectedSupplierDetails, setSelectedSupplierDetails] = useState(null);

  const options = supplierID.map((supplier) => ({
    value: supplier.id,
    label: supplier.supplierName,
  }));

  const handleChange = (selectedOption) => {
    const selectedSupplier = supplierID.find(
      (supplier) => supplier.id === selectedOption.value
    );
    setDataS(selectedOption ? selectedOption.value : "");
    setSelectedSupplierDetails(selectedSupplier);
    setIsReportGenerated(false);
    setSupplierItems([]);
  };

  const customStyles = {
    headCells: {
      style: {
        fontSize: "0.92rem",
        fontWeight: 610,
      },
    },
    rows: {
      style: {
        fontSize: "0.9rem",
      },
    },
  };

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => (row.id === "total-row" ? "" : index + 1),
      sortable: true,
      width: "80px",
    },

    {
      name: "Purchase Name",
      selector: (row) => (
        <Link to={`/menu-item/detail/${row.purchaseItemId}`}>
          {row.itemName}
        </Link>
      ),
      sortable: true,
    },
    {
      name: "Quantity",
      selector: (row) => row.quantity,
      sortable: true,
      right: true,
    },
    {
      name: "Amount",
      selector: (row) => row.amount,
      sortable: true,
      cell: (row) => <NumberCommas number={row.amount} />,
      right: true,
    },
    {
      name: "Actions",
      cell: (row) =>
        row.id === "total-row" ? null : (
          <Link to={`/menu-item/detail/${row.purchaseItemId}`}>
            <Button color="btn btn-soft-success" className="btn-sm gap-1">
              <i className="bx bx-show" />
            </Button>
          </Link>
        ),
      omit: isPrinting,
    },
  ];

  async function fetchPurchaseItems(supplier_id) {
    try {
      setLoading(true);
      const response = await ItemWiseServices.getList(supplier_id);
      setSupplierItems(response);
      setIsReportGenerated(true);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching purchase items:", error);
      toast.error(error.response?.data || "Error fetching purchase items");
      setLoading(false);
      setIsReportGenerated(false);
    }
  }

  async function fetchSuppliers() {
    try {
      const fetchedSuppliers = await SupplierServices.getList();
      setSupplierID(fetchedSuppliers?.data);
    } catch (error) {
      toast.error(error.response?.data || "Error fetching suppliers");
    }
  }

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const generateReport = () => {
    fetchPurchaseItems(dataS);
  };

  const handlePrint = () => {
    setIsPrinting(true);
    setTimeout(() => {
      window.print();
      setIsPrinting(false);
    }, 500);
  };

  const totalQuantity = supplierItems.reduce(
    (sum, row) => sum + row.quantity,
    0
  );
  const totalAmount = supplierItems.reduce((sum, row) => sum + row.amount, 0);

  const totalRow = {
    purchaseName: "Total",
    quantity: totalQuantity,
    amount: totalAmount,
    id: "total-row",
  };

  const tableData =
    supplierItems.length > 0 ? [...supplierItems, totalRow] : supplierItems;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="ItemWise Report"
          pageTitle="Itemwise"
        />
        <Row>
          <Col>
            <Card>
              <CardBody>
                <PrintHeader reportTitle="Supplier Purchased Items Report" />
                <Row className="d-print-none">
                  <FormGroup row>
                    <Col mb={2}>
                      <Label for="supplierID">Suppliers:</Label>
                    </Col>
                    <Col mb={4}>
                      <Select
                        value={options.find((option) => option.value === dataS)}
                        onChange={handleChange}
                        options={options}
                      />
                    </Col>
                    <Col mb={3}>
                      <Button
                        color="primary"
                        onClick={generateReport}
                        disabled={!dataS}
                      >
                        Generate Report
                      </Button>
                    </Col>
                    <Col mb={3} className="text-end">
                      {isReportGenerated && (
                        <Button
                          color="outline-secondary"
                          className="py-2 px-3 d-flex align-items-center shadow-sm ms-auto"
                          onClick={handlePrint}
                        >
                          <i className="ri-printer-line fs-16"></i>
                        </Button>
                      )}
                    </Col>
                  </FormGroup>
                </Row>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                {isPrinting ? (
                  <PrintableTable supplierItems={supplierItems} />
                ) : (
                  <DataTable
                    columns={columns}
                    data={tableData}
                    customStyles={customStyles}
                    pagination
                    responsive
                    striped
                    highlightOnHover
                    paginationPerPage={100}
                    paginationRowsPerPageOptions={[10, 25, 50, 100, 200, 500]}
                    progressPending={loading}
                    progressComponent={
                      <div className="my-3">
                        <Triangle
                          visible={true}
                          height="80"
                          width="80"
                          color="#5B71B9"
                          ariaLabel="triangle-loading"
                        />
                        <h5 className="mt-1">Loading...</h5>
                      </div>
                    }
                    noDataComponent={
                      <div className="text-center p-4">
                        <h5>No data available</h5>
                      </div>
                    }
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ItemWiseReport;
